import $ from "jquery"
// We actually need some of the plugins here to be loaded.
// Because of tree-shaking, this is _NOT_ equivalent to the other import below.
import "../selectize"
// eslint-disable-next-line no-duplicate-imports
import { SelectizePlugin } from "../selectize"
import { PNodeRegular } from "purview/dist/types/ws"
import { ARRAY_DELIMITER } from "common/src/constants"
import {
  Position,
  EMAIL_REGEX,
  SELECTIZE_DROPDOWN_HIDDEN_OPTION_VALUE,
} from "common/src/common"

interface Option {
  text: string | number
  value: string | number
  optgroup?: string
  subtext?: string
  icons?: string | string[]
  tooltip?: string
  iconPosition?: Position
  disabled?: boolean
  detail?: string
  interactive?: boolean
  unreadCount?: number
  showSeparator?: boolean
  heapClass?: string
  questionMarkTooltip?: string
}

// value key in Optgroup corresponds to optgroup key in Option
interface Optgroup {
  value: string
  label: string
}

interface RenderOptions {
  showSubtext?: boolean
  showDeleteButton?: boolean
  showDetail?: boolean
}

class SelectizeDropdown extends HTMLElement {
  // Attributes must be lowercase here.
  static observedAttributes = [
    "options",
    "value",
    "disabled",
    "multiple",
    "create",
    "createdisabled",
    "invalidvalues",
    "placeholder",
    "invalid",
    "clearinput",
    "lockoptgrouporder",
    "preserveoptionsorder",
    "searchurl",
  ]

  protected input: HTMLInputElement | HTMLSelectElement
  protected initialized: boolean = false
  protected shouldFocus: boolean = false
  protected invalidValues: string[] = []

  // Set by Purview.
  protected _vNode?: PNodeRegular

  focus(): void {
    if (this.initialized) {
      this.applyFocus()
    } else {
      this.shouldFocus = true
    }
  }

  applyFocus(): void {
    this.shouldFocus = false
    this.input.selectize.focus()
  }

  reloadOptions(): void {
    const { selectize } = this.input
    const wasOpened = selectize.$control_input.is(":focus")
    // Closing dropdown before reloading options to prevent layout reflow.
    // When selectize.updateOption(value, data) method is called, it will
    // re-render each option, triggering layout reflow if options are visible.
    // We want to avoid this. https://selectize.dev/docs.html#methods
    if (wasOpened) {
      selectize.close()
    }

    const options: Array<{ text: string; value: string }> = JSON.parse(
      this.getAttribute("options") as string,
    )

    // Unless `clearinput` is set, we return early if there are no options
    // because a reload will remove existing values from the component without
    // rerendering them, causing an entered value to disappear upon entry.
    if (options.length === 0 && !this.hasAttribute("clearinput")) {
      return
    }

    // N.B. We update options rather than using selectize.clearOptions() here
    // since doing so can prevent options from being selected. Such newly added
    // options change the options attribute, but if one of these new options is
    // selected then the options are reloaded. This clears the options and
    // triggers another change event with an empty value, clearing the dropdown.
    const values = new Set()
    options.forEach((option, index) => {
      values.add(option.value)
      let optionToAdd: {
        text: string
        value: string
        $order?: number
      } = option
      if (this.hasAttribute("preserveoptionsorder")) {
        // We add 1 here because the selectize $order property is 1-indexed.
        optionToAdd = { ...option, $order: index + 1 }
      }
      if (selectize.options[option.value] !== undefined) {
        selectize.updateOption(option.value, optionToAdd)
      } else {
        selectize.addOption(optionToAdd)
      }
    })

    Object.keys(selectize.options).forEach(value => {
      if (!values.has(value)) {
        if (selectize.items.includes(value)) {
          // Silently remove the item.
          selectize.removeItem(value, true)
        }
        selectize.removeOption(value)
      }
    })

    if (this.hasAttribute("value")) {
      this.setValue(this.getAttribute("value")!)
    }

    if (wasOpened) {
      selectize.open()
    }
  }

  attributeChangedCallback(
    name: string,
    _: string,
    newValue: string | null,
  ): void {
    if (!this.initialized) {
      return
    }

    const { selectize } = this.input
    if (name === "options") {
      this.reloadOptions()
    } else if (name === "value" && newValue !== null) {
      // Options are lost when searching using autocomplete and then deleting
      // the selected item using the delete key. When a newvalue of empty string
      // is passed, the options are reloaded to prevent the option from disappearing.
      if (newValue === "") {
        this.reloadOptions()
      }
      this.setValue(newValue)
    } else if (name === "value" && newValue === null) {
      selectize.clear(true)
      // HACK: this fixes a selectize bug that cuts off the input when reusing
      // a selectize-dropdown with new options and `null` as new value
      const input = this.querySelector("input")!
      input.style.position = "relative"
      input.style.left = "0px"
    } else if (name === "disabled") {
      if (newValue !== null) {
        selectize.disable()
      } else {
        selectize.enable()
      }
    } else if (
      name === "multiple" ||
      name === "create" ||
      name === "placeholder" ||
      name === "searchurl"
    ) {
      const $controlInput = this.input.selectize.$control_input
      if ($controlInput.is(":focus")) {
        this.shouldFocus = true
      }
      this.input.selectize.destroy()

      this.innerHTML = ""
      this.initialized = false
      this.connectedCallback()
    } else if (name === "invalidvalues") {
      this.invalidValues = newValue ? JSON.parse(newValue) : []

      // Manually update selectize's rendered item cache when invalid values
      // change otherwise nothing will update on screen.
      const items: HTMLElement[] = Object.values(
        // @ts-expect-error: Selectize..
        selectize.renderCache.item,
      )
      for (const item of items) {
        if (
          this.invalidValues.some(i => i === item.getAttribute("data-value"))
        ) {
          item.classList.add("invalid")
        } else {
          item.classList.remove("invalid")
        }
      }
    } else if (name === "invalid") {
      if (newValue === "") {
        this.input.selectize.$wrapper.addClass("invalid")
      } else {
        this.input.selectize.$wrapper.removeClass("invalid")
      }
    }
  }

  setValue(value: string): void {
    const { selectize } = this.input
    if (this.hasAttribute("multiple")) {
      selectize.setValue(value.split(ARRAY_DELIMITER), true)
    } else {
      selectize.setValue(value, true)
    }
  }

  connectedCallback(): void {
    if (this.initialized) {
      return
    }
    this.initialized = true

    const plugins: Array<Selectize.IPluginOption & { name: SelectizePlugin }> =
      []

    // N.B. Due to the drag_drop plugin overriding this.setup with the Selectize
    // prototype setup, ensure the drag_drop plugin is the first element in the
    // plugins array.
    const multiple = this.hasAttribute("multiple")
    if (multiple) {
      this.input = document.createElement("input")
      this.input.classList.add("v1-input")
      plugins.push({ name: "drag_drop", options: {} })
    } else {
      this.input = document.createElement("select")
    }

    if (this.hasAttribute("actionoptions")) {
      const actionOptions = JSON.parse(this.getAttribute("actionoptions")!)
      plugins.push({
        name: "dropdown_action_options",
        options: { actionOptions },
      })
    }

    const placeholder = this.getAttribute("placeholder")
    if (typeof placeholder === "string") {
      this.input.setAttribute("placeholder", placeholder)
    }

    const name = this.getAttribute("name")
    if (name) {
      this.input.setAttribute("name", name)
    }
    this.appendChild(this.input)

    const options: Option[] = JSON.parse(this.getAttribute("options")!)

    let optgroups: Optgroup[] = []
    if (this.hasAttribute("optgroups")) {
      optgroups = JSON.parse(this.getAttribute("optgroups")!)
    }

    if (this.hasAttribute("invalidvalues")) {
      this.invalidValues = JSON.parse(this.getAttribute("invalidvalues")!)
    }

    const create = this.hasAttribute("create")
    const regexType = this.getAttribute("regextype")
    const caseInsensitive = this.hasAttribute("caseinsensitive")
    const preventDuplicates = this.hasAttribute("preventduplicates")
    const lockOptgroupOrder = this.hasAttribute("lockoptgrouporder")

    let createFunction
    if (create && caseInsensitive) {
      createFunction = (input: string): Option => {
        const lowerCaseInput = input.toLowerCase()
        return { value: lowerCaseInput, text: lowerCaseInput }
      }
    }

    const createFilter = (newValue: string): boolean => {
      if (create) {
        if (this.hasAttribute("createdisabled")) {
          return false
        }
        if (regexType === "email") {
          return EMAIL_REGEX.test(newValue)
        }

        if (preventDuplicates) {
          const existing = this.input.selectize.items.find(
            item => item.toLowerCase() === newValue.toLowerCase(),
          )
          if (existing !== undefined) return false
        }

        return true
      }

      // Support copy / paste
      return options.some(o => o.value === newValue)
    }

    const config: Selectize.IOptions<unknown, unknown> = {
      options,
      optgroups,
      // Highlighting is turned off to prevent a bug in selectize. See
      // https://github.com/orumhq/orum/pull/614
      highlight: false,
      plugins,

      // For multi-selects where create is false, we still need to allow
      // creating to support pasting text. We use a createFilter() to limit
      // the possible options to only those that exist.
      create: createFunction ?? (create || multiple),
      createFilter,
      delimiter: ARRAY_DELIMITER,
      onChange: (): void => {
        // Update the value attribute in the virtual node to reflect the current
        // value. If we don't do this, and there's a re-render with the old
        // value, the attributeChangedCallback() won't trigger, since the value
        // attribute won't be modified.
        if (this._vNode) {
          const { attrs = {} } = this._vNode.data
          // Only update value if this is controlled.
          if (attrs.hasOwnProperty("value")) {
            attrs.value = this.input.value
            // This is needed to make sure the value attribute in the real DOM
            // stays in sync with the virtual DOM. Otherwise, we may not
            // receive an attributeChangedCallback if the value is reverted.
            this.setAttribute("value", this.input.value)
          }
        }
        const event = new Event("change", { bubbles: true })
        this.input.dispatchEvent(event)
      },
      render: {
        item: (data: Option, _) =>
          this.handleRender(data, {
            showDeleteButton: multiple,
            showSubtext: false,
            showDetail: false,
          }),
        option: (data: Option, _) =>
          this.handleRender(data, {
            showSubtext: true,
            showDetail: true,
          }),
      },
      maxOptions: 2000,
      disabledField: "disabled",
      persist: false,
      lockOptgroupOrder,
    }

    let value = null
    if (this.hasAttribute("value")) {
      value = this.getAttribute("value")
    } else if (this.hasAttribute("defaultValue")) {
      value = this.getAttribute("defaultValue")
    } else if (this.hasAttribute("default-value")) {
      value = this.getAttribute("default-value")
    }

    if (value !== null) {
      const delimiter = config.delimiter as string
      config.items = multiple ? value.split(delimiter) : [value]
    }

    const searchURL = this.getAttribute("searchurl")
    if (searchURL) {
      config.load = (term, callback) => {
        if (!term.trim()) {
          callback()
          return
        }

        void $.ajax({
          method: "GET",
          url: searchURL,
          data: { term },
          error: () => callback(),
          success: results => callback(results),
        })
      }
    }

    $(this.input).selectize(config)

    // Prevent clicks from propagating so we can use the dropdown in components
    // that also listen for clicks.
    const $wrapper = this.input.selectize.$wrapper
    $wrapper.on("click", e => e.stopPropagation())

    // If we have a multi-select where create is true, and we enter in some text
    // and then blur the input, the change event on selectize's internal input
    // triggers. This event propagates and causes Purview's event handler to
    // run. We don't want this to occur, so we stop propagation.
    const $controlInput = this.input.selectize.$control_input
    $controlInput.on("change", e => e.stopPropagation())

    if (this.hasAttribute("disabled")) {
      this.input.selectize.disable()
    }
    if (this.hasAttribute("invalid")) {
      this.input.selectize.$wrapper.addClass("invalid")
    }
    if (this.shouldFocus || this.hasAttribute("expanded")) {
      this.applyFocus()
    }
    const dropdownHeapClass = this.getAttribute("dropdownHeapClass")
    if (dropdownHeapClass !== null) {
      const selectizeInputs = $wrapper.find(".selectize-input")
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (selectizeInputs?.length > 0) {
        selectizeInputs[0].classList.add(dropdownHeapClass)
      }
    }
  }

  handleRender(data: Option, options: RenderOptions): string {
    const isInvalid = this.invalidValues.some(v => v === data.value)
    const rootElement = buildOptionElement({ data, options, isInvalid })
    return rootElement.outerHTML
  }
}

interface BuildOptionElementParams {
  data: Option
  options?: RenderOptions
  isInvalid?: boolean
}
export function buildOptionElement(
  params: BuildOptionElementParams,
): HTMLElement {
  const { data, options, isInvalid } = params
  let rootElement = document.createElement("div")
  rootElement.innerText = String(data.text)

  if (data.value === SELECTIZE_DROPDOWN_HIDDEN_OPTION_VALUE) {
    rootElement.setAttribute("style", "display: none;")
    return rootElement
  }

  if (isInvalid === true) {
    rootElement.className = "selectize-dropdown-text invalid"
  } else {
    rootElement.className = "selectize-dropdown-text"
  }

  // Append a detail item if specified.
  // Detail items are appended to sit *alongside* the text of each dropdown item, on the right.
  if (options?.showDetail === true && data.detail) {
    const detail = document.createElement("span")
    detail.className = "detail ml0"
    detail.innerText = data.detail

    rootElement.append(detail)
  }

  // Append a delete buttons if specified.
  // Delete buttons are appended to sit *alongside* the text of each dropdown item, on the right.
  if (options?.showDeleteButton === true) {
    const removeIcon = document.createElement("i")
    removeIcon.className = "fa fa-times delete"

    rootElement.append(removeIcon)
  }

  // Append icons if specified.
  // Icons sit *alongside* the text of each dropdown option, either on the left or right.
  if (data.icons !== undefined) {
    let iconElements: HTMLElement[]
    const iconMargin = data.iconPosition === Position.RIGHT ? "ml0" : "mr0"
    const buildIconElement = (iconClasses: string) => {
      const iconElement = document.createElement("i")
      // Put the default margin *before* the icon classes, so that the icon classes get precedence
      iconElement.className = `${iconMargin} ${iconClasses}`
      return iconElement
    }

    if (Array.isArray(data.icons)) {
      iconElements = data.icons.map(icon => buildIconElement(icon))
    } else {
      iconElements = [buildIconElement(data.icons)]
    }

    const iconElementWrapper = document.createElement("div")
    iconElementWrapper.className = "fr aic mw100"

    if (data.iconPosition === Position.RIGHT) {
      iconElementWrapper.append(rootElement)
      iconElementWrapper.append(...iconElements)
    } else {
      iconElementWrapper.append(...iconElements)
      iconElementWrapper.append(rootElement)
    }

    // Turn the icon wrapper into the outermost element
    rootElement = iconElementWrapper
  }

  // Further wrap with an unread badge if specified.
  // Unread badges sit *alongside* the text of each dropdown option, on the right.
  if (data.unreadCount !== undefined && data.unreadCount > 0) {
    const unreadCountWrapper = document.createElement("div")
    unreadCountWrapper.className = "fr"

    const unreadCount = document.createElement("div")
    unreadCount.className = "unread-count w100"
    unreadCount.innerText = String(data.unreadCount)

    // Turn the unread wrapper into the outermost element
    unreadCountWrapper.append(rootElement, unreadCount)
    rootElement = unreadCountWrapper
  }

  // Further wrap with selectize subtext if specified.
  // Subtext is appended to sit *under* the text of each dropdown option.
  if (options?.showSubtext === true && data.subtext) {
    const selectizeSubtextWrapper = document.createElement("div")
    selectizeSubtextWrapper.className = "selectize-subtext-option"

    const selectizeSubtext = document.createElement("div")
    selectizeSubtext.className = "desc"
    selectizeSubtext.innerText = data.subtext

    // Turn the subtext wrapper into the outermost element
    selectizeSubtextWrapper.append(rootElement, selectizeSubtext)
    rootElement = selectizeSubtextWrapper
  }

  // Further wrap with a tooltip if specified.
  if (data.tooltip) {
    const tooltipWrapper = document.createElement("div")
    tooltipWrapper.className = "selectize-tooltip-option"

    const tippyTooltip = document.createElement("tippy-tooltip")
    tippyTooltip.setAttribute("placement", "right")
    tippyTooltip.setAttribute("content", data.tooltip)

    if (data.interactive === true) {
      tippyTooltip.setAttribute("interactive", "")
    }

    // Turn the tooltip wrapper into the outermost element
    tippyTooltip.append(rootElement)
    tooltipWrapper.append(tippyTooltip)
    rootElement = tooltipWrapper
  }

  // Appends the question mark icon to the right of the text and adds a tooltip.
  if (data.questionMarkTooltip !== undefined) {
    const tooltipWrapper = document.createElement("div")
    tooltipWrapper.className = "selectize-tooltip-option"

    const tippyTooltip = document.createElement("tippy-tooltip")
    tippyTooltip.setAttribute("placement", "top")
    tippyTooltip.setAttribute("zIndex", "10001")
    tippyTooltip.setAttribute("content", data.questionMarkTooltip)

    if (data.interactive === true) {
      tippyTooltip.setAttribute("interactive", "")
    }

    const icon = document.createElement("i")
    icon.className = "far fa-question-circle ml1"
    tippyTooltip.append(icon)

    rootElement.append(tippyTooltip)
    tooltipWrapper.append(rootElement)
    rootElement = tooltipWrapper
  }

  if (data.showSeparator === true) {
    rootElement.classList.add("separator")
  }

  if (data.heapClass !== undefined) {
    rootElement.classList.add(data.heapClass)
  }

  return rootElement
}

customElements.define("selectize-dropdown", SelectizeDropdown)
